import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import type { CommunityModules } from './types';
import type { h } from 'preact';

type IconFn = () => h.JSX.Element;

function BookIcon() {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      style="width: 40px; height: 40px"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      style="width: 40px; height: 40px"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
      />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      style="width: 40px; height: 40px"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

const MODULE_STYLES: Array<{
  gradient: string;
  iconColor: string;
  Icon: IconFn;
}> = [
  { gradient: 'from-indigo-100 to-blue-50', iconColor: 'text-indigo-400', Icon: BookIcon },
  { gradient: 'from-emerald-100 to-teal-50', iconColor: 'text-emerald-400', Icon: ChartIcon },
  { gradient: 'from-violet-100 to-purple-50', iconColor: 'text-violet-400', Icon: SparklesIcon },
];

export function LibrarySidebar({ modules }: { modules: CommunityModules }) {
  if (modules.length === 0) {
    return undefined;
  }

  return (
    <div class="bg-indigo-50/40 dark:bg-gray-800/60 rounded-2xl p-5">
      <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
        Resources
      </h3>

      {modules.map((mod, i) => {
        const style = MODULE_STYLES[i % MODULE_STYLES.length];
        return (
          <a
            key={mod.id}
            href={`/courses/${AIP_COURSE_ID}/modules/${mod.id}`}
            class="block bg-white dark:bg-gray-700 rounded-xl overflow-hidden mb-3 hover:shadow-md transition no-underline group"
          >
            <div
              class={`h-24 bg-gradient-to-br ${style.gradient} flex items-center justify-center ${style.iconColor}`}
            >
              <style.Icon />
            </div>
            <div class="p-3">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition">
                {mod.title}
              </h4>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {mod.lessonCount} {mod.lessonCount === 1 ? 'lesson' : 'lessons'}
              </p>
            </div>
          </a>
        );
      })}

      <a
        href={`/courses/${AIP_COURSE_ID}`}
        class="flex items-center justify-center gap-1.5 mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 no-underline transition"
      >
        Browse all resources
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2.5"
          stroke="currentColor"
          style="width: 14px; height: 14px; flex-shrink: 0"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </a>
    </div>
  );
}
